import React from "react";
import { HEBREW_BASE_LETTERS, HEBREW_FINAL_FORMS } from "@/lib/hebrewCryptogram";

const ROWS = [
  ["א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט", "י"],
  ["כ", "ל", "מ", "נ", "ס", "ע", "פ", "צ", "ק"],
  ["ר", "ש", "ת"],
];
const FINALS = ["ך", "ם", "ן", "ף", "ץ"];

const PALETTES = {
  dark: {
    panel: "#0C0826",
    border: "#1A0F3C",
    key: "#241246",
    letter: "#F2D589",
    util: "#F5EFE3",
    muted: "#C6BBA9",
  },
  light: {
    panel: "#FFFBF2",
    border: "#EBE3D5",
    key: "#FFFFFF",
    letter: "#9A6E12",
    util: "#5C5268",
    muted: "#8C8198",
  },
};

export default function HebrewKeyboard({ onLetter, onSpace, onBackspace, disabled, theme = "dark" }) {
  const c = PALETTES[theme] || PALETTES.dark;
  const handleClick = (ch) => {
    if (disabled) return;
    onLetter && onLetter(ch);
  };

  return (
    <div
      data-testid="hebrew-keyboard"
      className="rounded-md border p-3 sm:p-4"
      style={{ borderColor: c.border, backgroundColor: c.panel }}
    >
      <div className="flex flex-col items-stretch gap-1.5" dir="rtl">
        {ROWS.map((row, ri) => (
          <div
            key={`row-${ri}`}
            className="flex justify-center gap-1 sm:gap-1.5"
          >
            {row.map((ch) => (
              <button
                key={ch}
                type="button"
                data-testid={`hkey-${ch}`}
                onClick={() => handleClick(ch)}
                disabled={disabled}
                className="flex-1 max-w-[44px] h-10 sm:h-11 rounded-md border text-lg sm:text-xl active:scale-95 transition-transform"
                style={{
                  backgroundColor: c.key,
                  borderColor: c.border,
                  color: c.letter,
                  fontFamily: "'Frank Ruhl Libre', serif",
                  fontWeight: 600,
                }}
              >
                {ch}
              </button>
            ))}
          </div>
        ))}

        <div className="flex items-center justify-center gap-1 sm:gap-1.5 mt-2 flex-wrap">
          <span
            className="text-[10px] uppercase tracking-[0.18em] ml-1"
            style={{ color: c.muted, fontFamily: "'Courier Prime', monospace" }}
          >
            Finals
          </span>
          {FINALS.map((ch) => (
            <button
              key={ch}
              type="button"
              data-testid={`hkey-${ch}`}
              onClick={() => handleClick(ch)}
              disabled={disabled}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-md border text-lg active:scale-95 transition-transform"
              style={{
                backgroundColor: c.key,
                borderColor: c.border,
                color: c.letter,
                fontFamily: "'Frank Ruhl Libre', serif",
                fontWeight: 600,
              }}
            >
              {ch}
            </button>
          ))}
          <button
            type="button"
            data-testid="hkey-backspace"
            onClick={() => !disabled && onBackspace && onBackspace()}
            disabled={disabled}
            className="px-3 h-9 sm:h-10 rounded-md border text-sm active:scale-95 transition-transform"
            style={{
              backgroundColor: c.key,
              borderColor: c.border,
              color: c.util,
              fontFamily: "'Courier Prime', monospace",
            }}
          >
            ⌫
          </button>
          <button
            type="button"
            data-testid="hkey-space"
            onClick={() => !disabled && onSpace && onSpace()}
            disabled={disabled}
            className="flex-1 min-w-[80px] max-w-[180px] h-9 sm:h-10 rounded-md border text-xs uppercase tracking-[0.18em] active:scale-95 transition-transform"
            style={{
              backgroundColor: c.key,
              borderColor: c.border,
              color: c.util,
              fontFamily: "'Courier Prime', monospace",
            }}
          >
            Space
          </button>
        </div>
      </div>
    </div>
  );
}

// Helpful exports for callers
export const HEBREW_KEYBOARD_LETTERS = [...HEBREW_BASE_LETTERS, ...Object.keys(HEBREW_FINAL_FORMS)];
